import React from 'react';
import {
  Editor as DraftEditor,
  EditorState,
  RichUtils
} from 'draft-js';
import isSoftNewlineEvent from 'draft-js/lib/isSoftNewlineEvent';
import {convertToHTML, convertFromHTML} from 'draft-convert';
import ImageBlockUrlModal from './ImageBlockUrlModal';
import '../content-editor/Editor.scss';

const styleMap = {
  LINK_BLUE: {
    color: 'blue',
    textDecoration: 'underline'
  }
};


class WysiwygBlock extends React.Component {
  constructor(props) {
    super(props);

    let editorState = null;

    if (this.props.value) {
      editorState = EditorState.createWithContent(convertFromHTML({
        htmlToStyle: (nodeName, node, currentStyle) => {
          if (nodeName === 'a') {
            return currentStyle.add('LINK_BLUE');
          }

          return currentStyle;
        },
        htmlToEntity: (nodeName, node, createEntity) => {
          if (nodeName === 'a') {
            return createEntity(
              'LINK',
              'MUTABLE',
              {
                url: node.getAttribute('href'),
                target: node.getAttribute('target')
              }
            );
          }
        }
      })(this.props.value));
    } else {
      editorState = EditorState.createEmpty();
    }

    this.state = {
      editorState: editorState,
      urlEditor: false,
      disabled: false,
      urlInfo: {}
    };

    this.handleEditorChange = this.handleEditorChange.bind(this);
    this.handleContentStateDataChange = this.handleContentStateDataChange.bind(this);
    this.handleKeyCommand = this.handleKeyCommand.bind(this);
    this.handleSetBlockType = this.handleSetBlockType.bind(this);
    this.handleToggleInlineStyle = this.handleToggleInlineStyle.bind(this);
    this.handleUrlToggle = this.handleUrlToggle.bind(this);
    this.handleReturn = this.handleReturn.bind(this);
    this.setUrl = this.setUrl.bind(this);
  }

  /*
  componentDidUpdate(prevProps) {
    // Update the internal editorState to match the blocks set in the
    // properties:
    const blocks = blocksInternalToDraft(this.props.blocks);
    const contentState = ContentState.createFromBlockArray(blocks);
    const newState = EditorState.createWithContent(contentState);
  }
  */

  handleEditorChange(newState, resetFocus) {
    const html = convertToHTML({
      entityToHTML: (entity, originalText) => {
        if (entity.type === 'LINK') {
          return <a href={entity.data.url} target={entity.data.target}>{originalText}</a>;
        }

        return originalText;
      }
    })(newState.getCurrentContent());

    if (!this.props.disabled) {
      this.props.onChange(html);

      this.setState({
        editorState: newState
      }, () => {
        if (resetFocus) {
          setTimeout(() => this.refEditor.focus(), 0);
        }
      });
    }
  }

  handleContentStateDataChange(contentState) {
    const newState = EditorState.push(
      this.state.editorState,
      contentState,
      'change-block-data'
    );

    return this.handleEditorChange(newState);
  }

  handleUrlToggle(e) {
    e.preventDefault();

    const {editorState} = this.state;
    const contentState = editorState.getCurrentContent();
    const startKey = editorState.getSelection().getStartKey();
    const startOffset = editorState.getSelection().getStartOffset();
    const blockWithLinkAtBeginning = contentState.getBlockForKey(startKey);
    const linkKey = blockWithLinkAtBeginning.getEntityAt(startOffset);

    let linkData;

    if (linkKey) {
      const linkInstance = contentState.getEntity(linkKey);
      linkData = linkInstance.getData();
    }

    this.setState({
      urlEditor: !this.state.urlEditor,
      urlInfo: linkData ? {
        url: linkData.url,
        urlInTab: linkData.target
      } : {}
    });
  }

  setUrl(url, urlInTab) {
    const {editorState} = this.state;

    if (!url) {
      let newEditorState = RichUtils.toggleLink(
        editorState,
        editorState.getSelection(),
        null
      );

      const currentStyle = newEditorState.getCurrentInlineStyle();
      if (currentStyle.has('LINK_BLUE')) {
        newEditorState = RichUtils.toggleInlineStyle(
          newEditorState,
          'LINK_BLUE'
        );
      }

      this.handleEditorChange(newEditorState, true);
      return;
    }

    const contentState = editorState.getCurrentContent();
    const contentStateWithEntity = contentState.createEntity(
      'LINK',
      'MUTABLE',
      {
        url: url,
        target: urlInTab ? '_blank' : undefined
      }
    );

    const entityKey = contentStateWithEntity.getLastCreatedEntityKey();
    let newEditorState = RichUtils.toggleLink(
      editorState,
      editorState.getSelection(),
      entityKey
    );

    const currentStyle = newEditorState.getCurrentInlineStyle();
    if (!currentStyle.has('LINK_BLUE')) {
      newEditorState = RichUtils.toggleInlineStyle(
        newEditorState,
        'LINK_BLUE'
      );
    }


    this.handleEditorChange(newEditorState, true);
  }

  handleSetBlockType(e, type) {
    e.preventDefault();

    const newState = RichUtils.toggleBlockType(this.state.editorState, type);

    this.handleEditorChange(newState, true);
  }

  handleToggleInlineStyle(e, style) {
    e.preventDefault();

    const newState = RichUtils.toggleInlineStyle(this.state.editorState, style);

    this.handleEditorChange(newState, true);
  }

  handleKeyCommand(command) {
    const newState = RichUtils.handleKeyCommand(this.state.editorState, command);
    if (newState) {
      this.handleEditorChange(newState);
      return 'handled';
    }
    return 'not-handled';
  }

  // Handle soft new lines..
  // https://github.com/facebook/draft-js/issues/234
  handleReturn = (evt) => {
    const blockType = RichUtils.getCurrentBlockType(this.state.editorState);

    if (blockType !== 'unstyled' || !isSoftNewlineEvent(evt)) {
      // Just to be sure, soft returns are executed only with unstyled blocks.
      return 'not_handled';
    }

    const newState = RichUtils.insertSoftNewline(this.state.editorState);

    this.handleEditorChange(newState);
    return 'handled';
  };

  render() {
    const {editorState} = this.state;
    const currentBlockType = RichUtils.getCurrentBlockType(editorState);
    const currentLink = RichUtils.currentBlockContainsLink(editorState);
    const currentStyle = editorState.getCurrentInlineStyle();
    const isSelection = !editorState.getSelection().isCollapsed();
    const disabled = this.props.disabled || this.state.disabled;

    return (
      <div className={`${disabled ? 'disabled ' : ''}content-editor`}>
        <div className="content-editor-actions">
          <div className="content-editor-actions-block">
            <button
              className={`${currentBlockType === 'header-one' ? 'active ' : ''}btn btn-link`}
              onClick={(e) => this.handleSetBlockType(e, 'header-one')}
              disabled={disabled}
            >
              H1
            </button>
            &nbsp;
            <button
              className={`${currentBlockType === 'header-two' ? 'active ' : ''}btn btn-link`}
              onClick={(e) => this.handleSetBlockType(e, 'header-two')}
              disabled={disabled}
            >
              H2
            </button>
            &nbsp;
            <button
              className={`${currentBlockType === 'header-three' ? 'active ' : ''}btn btn-link`}
              onClick={(e) => this.handleSetBlockType(e, 'header-three')}
              disabled={disabled}
            >
              H3
            </button>
            &nbsp;
            {/*
            <button
              className={(currentBlockType === 'header-four' ? 'active ' : '') + 'btn btn-link'}
              onClick={e => {
                this.handleSetBlockType(e, 'header-four');
              }}
              disabled={disabled}
            >
              H4
            </button>
            &nbsp;
            <button
              className={(currentBlockType === 'header-five' ? 'active ' : '') + 'btn btn-link'}
              onClick={e => this.handleSetBlockType(e, 'header-five')}
              disabled={disabled}
            >
              H5
            </button>
            &nbsp;
            <button
              className={(currentBlockType === 'header-six' ? 'active ' : '') + 'btn btn-link'}
              onClick={e => this.handleSetBlockType(e, 'header-six')}
              disabled={disabled}
            >
              H6
            </button>
            &nbsp;
            */}
            <button
              className={`${currentBlockType === 'unordered-list-item' ? 'active ' : ''}btn btn-link`}
              onClick={(e) => this.handleSetBlockType(e, 'unordered-list-item')}
              disabled={disabled}
            >
              UL
            </button>
            &nbsp;
            <button
              className={`${currentBlockType === 'ordered-list-item' ? 'active ' : ''}btn btn-link`}
              onClick={(e) => this.handleSetBlockType(e, 'ordered-list-item')}
              disabled={disabled}
            >
              OL
            </button>
            &nbsp;
            <button
              className={`${currentBlockType === 'blockquote' ? 'active ' : ''}btn btn-link`}
              onClick={(e) => this.handleSetBlockType(e, 'blockquote')}
              disabled={disabled}
            >
              Sitat
            </button>
            &nbsp;
            <button
              className={`${currentBlockType === 'unstyled' ? 'active ' : ''}btn btn-link`}
              onClick={(e) => this.handleSetBlockType(e, 'unstyled')}
              disabled={disabled}
            >
              P
            </button>
            &nbsp;
            <button
              className={`${currentStyle.has('BOLD') ? 'active ' : ''}btn btn-link`}
              onClick={(e) => this.handleToggleInlineStyle(e, 'BOLD')}
              disabled={disabled}
            >
              <strong>F</strong>
            </button>
            &nbsp;
            <button
              className={`${currentStyle.has('ITALIC') ? 'active ' : ''}btn btn-link`}
              onClick={(e) => this.handleToggleInlineStyle(e, 'ITALIC')}
              disabled={disabled}
            >
              <em>K</em>
            </button>
          </div>
          {/*
          <button
            className={`${currentStyle.has('UNDERLINE') ? 'active ' : ''}btn btn-link`}
            onClick={(e) => this.handleToggleInlineStyle(e, 'UNDERLINE')}
            disabled={disabled}
          >
            <u>U</u>
          </button>
          &nbsp;
          */}
          <div className="content-editor-actions-insert">
            <button
              className={`${currentLink ? 'active ' : ''}btn btn-link`}
              onClick={this.handleUrlToggle}
              disabled={disabled || !isSelection}
            >
              Link
            </button>
            &nbsp;
          </div>
        </div>
        <div className="content-editor-input">
          <DraftEditor
            editorState={editorState}
            onChange={this.handleEditorChange}
            customStyleMap={styleMap}
            handleKeyCommand={this.handleKeyCommand}
            handleReturn={this.handleReturn}
            ref={(ref) => (this.refEditor = ref)}
            readOnly={disabled}
          />
        </div>
        {this.state.urlEditor && <ImageBlockUrlModal
          show={this.state.urlEditor}
          onHide={() => this.setState({urlEditor: false})}
          setUrl={this.setUrl}
          url={this.state.urlInfo.url}
          urlInTab={this.state.urlInfo.urlInTab}
        />}
      </div>
    );
  }
}

WysiwygBlock.defaultProps = {
  onChange: () => {},
  value: '',
  disabled: false
};

export default WysiwygBlock;
